import { Injectable, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {EnvDescriptor, EnvLoader} from 'app/Environment/EnvLoader';
import {CartService} from '../Cart/CartService';
import {FoodProfile, User} from './auth.service';
import {PaymentWay} from './payment-wall.service';
import {TokenService} from './token.service';

export interface Company {
  id: number;
  name: string;
  reference: string;
  customerId: number;
  deliveryRound: string | null;
  isAcceptPayment: boolean;
  menuTypes: any[]; // Si vous avez des détails sur la structure de `menuTypes`, vous pouvez la remplacer par un type plus précis
  endOfServiceCategories: any[]; // De même pour `endOfServiceCategories` si nécessaire
  wasteDatasCategories: any[]; // Idem pour `wasteDatasCategories`
  reservationDay: number;
  modificationDay: string | null;
  numberOfDaysForModification: number | null;
  numberOfDaysForCancelation: number;
  shoudHavePresenteeism: boolean;
  orderMaxHour: string; // Date ISO 8601 en string
  updateMaxHour: string; // Date ISO 8601 en string
  isUniqDayForOrder: boolean;
  orderMinWeek: number;
  orderMaxWeek: number;
  cgvPath: string | null;
}

export interface ProfessionalSocialCategory {
  id: number;
  label: string;
  name: string;
  userCompanyData: any[];
  userEnterpriseData: string[];
  customerId: number;
}

export interface Admission {
  id: number;
  label: string;
  description: string;
  companyMenuOptionItems: string[];
  content: any[];
  max: number;
  customerId: number;
  price: number;
  vat: number;
  priceInCents: number;
  vatPrice: number;
  mealmoment: {
    id: number;
    name: string;
    gpaoId: string;
    ordre: number;
    customerId: number;
    qdmId: number;
    code: string;
  };
}

export interface City {
  id: number;
  name: string;
  country: string;
  __initializer__: any | null;
  __cloner__: any | null;
  __isInitialized__: boolean;
}

export interface Shift {
  id: number;
  name: string;
  enterprises: string[];
  customerId: number;
  createdAt: string;
}

export interface Periodicity {
  id: number;
  dayCode: number;
  daysBefore: number;
  hoursBefore: string;
  enterprises: string[];
  customerId: number;
  createdAt: string;
  mealMoment: {
    code: string; // Code du moment repas (ex: "MIDI")
    customerId: number; // Identifiant du client associé
    gpaoId: string; // Identifiant GPao
    id: number; // Identifiant du moment repas
    name: string; // Nom du moment repas (ex: "MIDI")
    ordre: number; // Ordre du moment repas
    qdmId: number; // Identifiant QDM
    __cloner__?: any; // Champ technique (nullable)
    __initializer__?: any; // Champ technique (nullable)
    __isInitialized__?: boolean; // Indique si l'objet est initialisé
  };
}

export interface DeliveryRound {
  id: number;
  label: string;
  name: string;
  companies: string[];
  customerId: number;
}

export interface Enterprise {
  id: number;
  name: string;
  customerId: number;
  admissions: Admission[];
  deliveryRound: DeliveryRound;
  email: string | null;
  postalCode: string;
  city: City;
  address: string;
  isAcceptPayment: boolean;
  isEnabled: boolean;
  autoFillFirstChoice: boolean;
  shoudHavePresenteeism: boolean;
  shifts: Shift[];
  periodicities: Periodicity[];
  minWallet: number;
  companies: Company[];

  paymentOrder: string[];
  paymentWallet: string[];
}

export interface EnterpriseResponse {
  id: number;
  canOrderMultiple: boolean;
  maxOrderNumber: number;
  professionalSocialCategory: ProfessionalSocialCategory;
  enterprises: Enterprise[];
  foodProfiles: FoodProfile[];
}

export interface FullAdmissionsData {
  userId: number;
  enterpriseId: number;
  admissionName: string;
  admissionDescription: string;
  admissionMax: number;
  admissionId: number;
  contributionPrice: number;
  admissionVat: number;
  admissionPrice: number;
  finalPrice: number;
  isFullMenu: boolean;
  admissionParts: AdmissionPart[];
  maxRequired: boolean | undefined;
  mealMoment: {
    id: number;
    name: string;
    gpao_id: string;
    ordre: number;
    customer_id: number;
    qdm_id: number;
    code: string;
  };
}

export interface AdmissionPart {
  quantity: number;
  serviceId: number;
  isMandatory: number;
  serviceName: string;
  serviceSort: number;
  serviceIsNotInAdmission: number;
  isLegume: number;
}

export interface Service {
  id: number;
  name: string;
  shortName: string;
  isNotInAdmission: boolean;
  sort: number;
  customerId: number;
  image?: string;
}

export interface OrderItem {
  createdAt: string;
  id: number;
  platId: number;
  quantity: number;
  order: string;
  serviceContent: string;
  service: Service;
  isExtra: boolean;
  vatPrice: number;
  platIdGpao: number | null;
  price: number;
  vat: number;
  priceInCents: number;
  priceWithoutVat: number;
}

export interface Payments {
  WALLET: number;
  PAYGREEN: Array<{ [key: string]: number }>;
}

export interface OrderHistoryObject {
  id: number;
  status: string;
  company: any;
  companyMenuOption: any;
  isPaid: boolean;
  orderItems: OrderItem[];
  deliveryDate: string;
  admissionPrice: number;
  contributionPrice: number;
  createdAt: string;
  payments: Payments;
  child_id: number | null;
  child_name: string | null;
  svc_id: number | null;
  price: number;
  vat: number;
  priceWithoutVat: number;
  isExpanded: boolean;
  comment: string | null;
  enterprise: {
    name: string;
  }
  mealmoment: {
    id: number;
    name: string;
    gpaoId: string;
    ordre: number;
    customerId: number;
    qdmId: number;
    code: string;
  };
  enterprisePlace: {
    id: number;
    name: string;
  };
  isOrderForGroup: boolean;
}

export interface BulkCompanyStatusResponse {
  [key: number]: { status: boolean };
}

@Injectable({providedIn: 'root'})
export class GestionService {
  private httpClient = inject(HttpClient);
  private cart = inject(CartService);
  private token = inject(TokenService);

  env: EnvDescriptor | null = null;

  constructor() {
    if ((globalThis as any).env) {
      this.env = (globalThis as any).env;
    } else {
      this.env = EnvLoader.load();
      (globalThis as any).env = this.env;
    }
  }

  userEnterprisesData: { [key: string]: EnterpriseResponse | null } = {};

  /**
   * Fetches enterprise data for a specified user
   * @param userId - The ID of the user whose enterprise data to retrieve
   * @returns Promise resolving to EnterpriseResponse object or null if request fails
   */
  fetchUserEnterpriseData(userId: number): Promise<EnterpriseResponse | null> {
    return new Promise((res, rej) => {
      if (this.userEnterprisesData[userId]) {
        res(this.userEnterprisesData[userId]);
        return;
      }

      this.httpClient
        .get<EnterpriseResponse>(
          `${this.env?.boGestionUrl}/api/user_enterprise_datas/user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${this.token.getTokenBoGestion()}`,
            },
          }
        )
        .subscribe(
          (data) => {
            this.userEnterprisesData = {
              ...this.userEnterprisesData,
              [userId]: data,
            };
            res(data);
          },
          (error) => {
            if (error.status === 0) rej(null);

            console.error(error);
            rej(null);
          }
        );
    });
  }

  /**
   * Fetches all available admission options for a specific user and enterprise
   * @param userId - The ID of the user
   * @param enterpriseId - The ID of the enterprise
   * @returns Promise resolving to array of FullAdmissionsData or null if request fails
   */
  fetchAllAdmissions(
    userId: number,
    enterpriseId: number
  ): Promise<FullAdmissionsData[]> {
    return new Promise((res, rej) => {
      this.httpClient
        .get<FullAdmissionsData[]>(
          `${this.env?.boGestionUrl}/api/company_menu_options/by_user/${userId}/${enterpriseId}`,
          {
            headers: {
              Authorization: `Bearer ${this.token.getTokenBoGestion()}`,
            },
          }
        )
        .subscribe(
          (data) => {
            if(!data || !Array.isArray(data)) {
              res([]);
              return;
            }

            // data is an array, edit all admissions, if parts is null, parts now equals to []
            data.forEach((admission) => {
              if (!admission.admissionParts) {
                admission.admissionParts = [];
              }
            });

            res(data);
          },
          (error) => {
            if (error.status === 0) rej(null);

            console.error(error);
            rej(null);
          }
        );
    });
  }

  /**
   * Calculates the maximum quantity allowed for a specific rank within an admission
   * @param admission - The admission data containing parts information
   * @param rankId - The rank/service ID to check
   * @param groupOrderQuantity - The quantity multiplier for group orders
   * @returns Maximum quantity allowed for the specified rank
   */
  getMaxQuantityForRankId(
    admission: FullAdmissionsData | null,
    rankId: number,
    groupOrderQuantity: number
  ): number {
    if (!admission) return 0;

    return (
      admission.admissionParts
        .filter((part) => part.serviceId === rankId)
        .reduce((acc, part) => acc + part.quantity, 0) * groupOrderQuantity
    );
  }

  /**
   * Checks if a service is mandatory within a specific admission
   * @param admission - The admission data containing parts information
   * @param rankId - The rank/service ID to check
   * @returns Boolean indicating whether the service is mandatory
   */
  isMandatoryService(
    admission: FullAdmissionsData | null,
    rankId: number
  ): boolean {
    if (!admission) return false;

    return admission.admissionParts
      .filter((part) => part.serviceId === rankId)
      .reduce((acc, part) => acc || !!part.isMandatory, false);
  }

  /**
   * Checks if a service is included in a specific admission
   * @param admission - The admission data containing parts information
   * @param rankId - The rank/service ID to check
   * @returns Boolean indicating whether the service is included in the admission
   */
  isInAdmission(admission: FullAdmissionsData | null, rankId: number): boolean {
    if (!admission) return false;

    return admission.admissionParts
      .filter((part) => part.serviceId === rankId)
      .reduce((acc, part) => acc || !part.serviceIsNotInAdmission, false);
  }

  /**
   * Calculates the remaining quantity available for a service in a specific date and admission
   * Takes into account cart items and special meal types (vegan, meat)
   * @param admission - The admission data
   * @param date - The date string for the order
   * @param serviceId - The service ID
   * @param childId - The child ID (if applicable)
   * @param rankId - The rank/service ID
   * @param groupOrderQuantity - The quantity multiplier for group orders
   * @returns Remaining quantity available
   */
  getRemainingQuantityFor(
    admission: FullAdmissionsData | null,
    date: string,
    serviceId: number,
    childId: number,
    rankId: number,
    groupOrderQuantity: number
  ): number {
    if (!admission) return 0;

    const admissionRank = admission.admissionParts.find(
      (p) => p.serviceId === rankId
    );
    if (!admissionRank) return 0;

    const cart = this.cart.readCartContent();
    const cartForDate = cart.find(
      (item) =>
        item.date === date &&
        item.serviceId === serviceId &&
        item.childId == childId
    );

    if (!cartForDate) return admissionRank.quantity * groupOrderQuantity;

    let remainingQty = cartForDate.items
      .filter((item) => item.rankId === rankId)
      .reduce((acc, item) => acc + item.quantity, 0);
    remainingQty = admissionRank.quantity * groupOrderQuantity - remainingQty;

    const fullVeganQty = cartForDate.items
      .filter((item) => item.isVeganFullCourse)
      .reduce((acc, item) => acc + item.quantity, 0);
    const fullMeatQty = cartForDate.items
      .filter((item) => item.isMeatFullCourse)
      .reduce((acc, item) => acc + item.quantity, 0);

    if (admissionRank.isLegume) {
      let delta = 0;

      if (fullMeatQty > 0) {
        delta += fullMeatQty;
      }

      if (fullVeganQty > 0) {
        delta += fullVeganQty;
      }

      remainingQty -= delta;
      if (remainingQty < 0) remainingQty = 0;

      return remainingQty;
    }

    return remainingQty;
  }

  private commandsHistoryCache: { [key: number]: OrderHistoryObject[] } = {};

  /**
   * Fetches order history for a specific company
   * @param companyId - The ID of the company
   * @param forceRefresh - Optional flag to force refresh the cache
   * @returns Promise resolving to array of order history objects
   */
  fetchCommandsHistory(
    companyId: number,
    forceRefresh: boolean = false
  ): Promise<OrderHistoryObject[]> {
    if (this.commandsHistoryCache[companyId] && !forceRefresh) {
      return Promise.resolve(this.commandsHistoryCache[companyId]);
    }

    return new Promise((res, rej) => {
      this.httpClient
        .get(
          `${this.env?.boGestionUrl}/api/orders/list_by_user_company/${companyId}`,
          {
            headers: {
              Authorization: `Bearer ${this.token.getTokenBoGestion()}`,
            },
          }
        )
        .subscribe({
            next: (data) => {
              let orders = ((data as any).orders || []) as OrderHistoryObject[];
              // orders = orders.filter(
              //   (order: OrderHistoryObject) => order.svc_id !== null
              // );
              this.commandsHistoryCache[companyId] = orders; // Cache the result
              res(orders);
            },
            error: (error) => {
              if (error.status === 0) rej(null);
              console.error(error);
              rej(null);
            }
          }
        );
    });
  }

  /**
   * Fetches invoice data for a specific order
   * @param orderId - The ID of the order
   * @returns Promise resolving to invoice data
   */
  fetchFacture(orderId: number): Promise<any> {
    return new Promise((res, rej) => {
      this.httpClient
        .get(`${this.env?.boGestionUrl}/api/orders/invoice_order/${orderId}`)
        .subscribe(
          (data) => {
            res(data);
          },
          (error) => {
            if (error.status === 0) rej(null);
            console.error(error);
            rej(null);
          }
        );
    });
  }

  /**
   * Cancels an existing order
   * @param orderId - The ID of the order to cancel
   * @returns Promise resolving to cancellation response data
   */
  cancelOrder(orderId: number): Promise<any> {
    return new Promise((res, rej) => {
      this.httpClient
        .get(`${this.env?.boGestionUrl}/api/orders/cancel_order/${orderId}`, {})
        .subscribe(
          (data) => {
            res(data);
          },
          (error) => {
            if (error.status === 0) rej(null);
            console.error(error);
            rej(null);
          }
        );
    });
  }

  /**
   * Submits a new order to the system
   * @param orderBody - The order data object
   * @returns Promise resolving to order confirmation data
   */
  pushOrder(orderBody: any): Promise<any> {
    return new Promise((res, rej) => {
      this.httpClient
        .post(
          `${this.env?.boGestionUrl}/api/orders/add?from=web_${window.location.hostname}`,
          orderBody
        )
        .subscribe(
          (data) => {
            res(data);
          },
          (error) => {
            console.error(error);
            rej(null);
          }
        );
    });
  }

  private companyIdCache: Map<string, number> = new Map();

  /**
   * Fetches company ID by restaurant ID and customer ID
   * @param restoId - The restaurant ID
   * @param cstId - The customer ID
   * @returns Promise resolving to company ID number
   */
  fetchCompanyId(restoId: number, cstId: number): Promise<number> {
    const cacheKey = `${restoId}-${cstId}`;
    const cachedId = this.companyIdCache.get(cacheKey);

    if (cachedId !== undefined) {
      return Promise.resolve(cachedId);
    }

    return new Promise((res, rej) => {
      this.httpClient
        .get(
          `${this.env?.boGestionUrl}/api/companies/ref/${restoId}?wso_id=2&cst_id=${cstId}`
        )
        .subscribe(
          (data) => {
            const companyId = (data as any).id as number;
            this.companyIdCache.set(cacheKey, companyId);
            res(companyId);
          },
          (error) => {
            if (error.status === 0) rej(null);
            console.error(error);
            rej(null);
          }
        );
    });
  }

  /**
   * Fetches complete company information
   * @param restoId - The restaurant ID
   * @param cstId - The customer ID
   * @returns Promise resolving to Company object
   */
  fetchCompany(restoId: number, cstId: number): Promise<Company> {
    return new Promise((res, rej) => {
      this.httpClient
        .get(
          `${this.env?.boGestionUrl}/api/companies/ref/${restoId}?wso_id=2&cst_id=${cstId}`,
          {
            headers: {
              Authorization: `Bearer ${this.token.getTokenBoGestion()}`,
            },
          }
        )
        .subscribe(
          (data) => {
            res(data as Company);
          },
          (error) => {
            if (error.status === 0) rej(null);
            console.error(error);
            rej(null);
          }
        );
    });
  }

  /**
   * Reloads user wallet with specified amount
   * @param amount - The amount to add to the wallet
   * @param userEnterpriseData - The user enterprise data ID
   * @param enterprise - The enterprise ID
   * @param payWay - The payment method to use
   * @returns Promise resolving to payment URL string
   */
  reloadWallet(
    amount: number,
    userEnterpriseData: number,
    enterprise: number,
    payWay: PaymentWay
  ): Promise<string> {
    return new Promise((res, rej) => {
      this.httpClient
        .post(
          `${this.env?.boGestionUrl}/api/orders/add-to-wallet?from=web_${window.location.hostname}`,
          {
            amount: amount,
            userEnterpriseData: userEnterpriseData,
            enterprise: enterprise,
            paymentWays: [payWay],
          }
        )
        .subscribe(
          (data) => {
            res((data as any).url as string);
          },
          (error) => {
            console.error(error);
            rej(null);
          }
        );
    });
  }

  private iconCache: { [key: number]: { url: string; bg: string } } = {};

  /**
   * Fetches icons for specified services
   * Uses local cache to avoid redundant requests
   * @param servicesIds - Array of service IDs to fetch icons for
   * @returns Promise resolving to mapping of service IDs to icon data
   */
  fetchServicesIcones(
    servicesIds: number[]
  ): Promise<{ [key: string]: { url: string; bg: string } }> {
    return new Promise((res, rej) => {
      const missingIds = servicesIds.filter((id) => !this.iconCache[id]);

      if (missingIds.length === 0) {
        const cachedData = servicesIds.reduce((result, id) => {
          result[id] = this.iconCache[id];
          return result;
        }, {} as { [key: string]: { url: string; bg: string } });
        res(cachedData);
        return;
      }

      this.httpClient
        .post(`${this.env?.boGestionUrl}/api/orders/getImagesForServices`, {
          services: missingIds,
        })
        .subscribe(
          (data) => {
            // Vérifie et met à jour le cache
            Object.entries(data).forEach(([key, value]) => {
              this.iconCache[+key] = value; // Convertit la clé en number pour correspondre au type attendu
            });

            const combinedData = servicesIds.reduce((result, id) => {
              result[id] = this.iconCache[id];
              return result;
            }, {} as { [key: string]: { url: string; bg: string } });

            res(combinedData);
          },
          (error) => {
            rej(null);
          }
        );
    });
  }

  /**
   * Saves user preferences
   * @param pref - The preferences object to save
   * @param userId - The ID of the user
   * @returns Promise resolving to updated User object
   */
  savePreferences(pref: any, userId: number) {
    return new Promise<User>((res) => {
      const stringifiedPref = JSON.stringify(pref);

      this.httpClient
        .put(
          `${this.env?.boGestionUrl}/api/users/${userId}`,
          {
            preferences: stringifiedPref,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }
        )
        .subscribe((data) => {
          res(data as User);
        });
    });
  }

  /**
   * Gets dates available for ordering within a specified time range
   * @param from - Start date string
   * @param to - End date string
   * @param enterpriseId - The enterprise ID
   * @param mealtime_gpao_id - The mealtime GPAO ID
   * @returns Promise resolving to array of available date strings
   */
  getOrderableDates(
    from: string,
    to: string,
    enterpriseId: number,
    mealtime_gpao_id: number
  ): Promise<string[]> {
    return new Promise((res) => {
      this.httpClient
        .post(
          `${this.env?.boGestionUrl}/api/orders/periodicities/next`,
          {
            from: from,
            to: to,
            mealtime_qdm_id: mealtime_gpao_id,
            enterprise_id: enterpriseId,
          },
          {
            headers: {
              Authorization: `Bearer ${this.token.getTokenBoGestion()}`,
            },
            observe: 'response',
          }
        )
        .subscribe((data) => {
          res(((data.body as any).dates as string[]) || []);
        });
    });
  }

  /**
   * Verifies if a specific date is valid according to periodicity rules
   * @param date - The date string to check
   * @param enterpriseId - The enterprise ID
   * @param mealtimeQdmId - The mealtime QDM ID
   * @returns Promise resolving to boolean indicating validity
   */
  checkPeriodcity(
    date: string,
    enterpriseId: number,
    mealtimeQdmId: number
  ): Promise<boolean> {
    return new Promise((res) => {
      this.httpClient
        .post(`${this.env?.boGestionUrl}/api/orders/periodicities/check`, {
          date: date,
          enterprise_id: enterpriseId,
          mealtime_qdm_id: mealtimeQdmId,
          goInThePast: null,
        })
        .subscribe((data) => {
          res((data as any).success as boolean);
        });
    });
  }

  fetchBulkCompanyStatus(companyIds: number[]): Promise<BulkCompanyStatusResponse> {
    return new Promise((res, rej) => {
      this.httpClient.post(`${this.env?.boGestionUrl}/api/companies/bulkCompanyStatus`, companyIds).subscribe((data) => {
        res(data as BulkCompanyStatusResponse);
      });
    });
  }
}
